import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trash2, UserPlus, ShieldCheck, User as UserIcon, Pencil } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const inputCls = "rounded-xl h-11";

const ROLE_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
];

export default function AdminTeam() {
  const { admin: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/team");
      setMembers(data.items || []);
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (me && me.role === "admin") fetchMembers();
  }, [fetchMembers, me]);

  // Only admin can access; render a friendly redirect for members
  if (me && me.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }

  const toggleActive = async (m) => {
    try {
      const { data } = await api.patch(`/admin/team/${m.id}`, { active: !m.active });
      setMembers((arr) => arr.map((x) => (x.id === m.id ? { ...x, ...data } : x)));
      toast.success(`${data.name} ${data.active ? "activated" : "deactivated"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    }
  };

  const removeMember = async (m) => {
    setMemberToDelete(m);
  };

  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;
    try {
      await api.delete(`/admin/team/${memberToDelete.id}`);
      setMembers((arr) => arr.filter((x) => x.id !== memberToDelete.id));
      toast.success("Team member deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setMemberToDelete(null);
    }
  };

  const onMemberCreated = (m) => {
    setMembers((arr) => [...arr, m]);
    setOpenForm(false);
    toast.success(`${m.name} added to the team`);
  };

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-team-page">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">Team</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-team-heading">
              Members
            </h1>
            <p className="mt-2 text-sm text-brand-slate/75">
              Invite teammates to work on leads. Only admins can manage the team.
            </p>
          </div>
          <button
            onClick={() => setOpenForm(true)}
            className="inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors"
            data-testid="admin-team-add-button"
          >
            <UserPlus size={14} /> Add member
          </button>
        </div>

        <div className="bg-white border border-brand-navy/10 overflow-hidden rounded-2xl" data-testid="admin-team-table-wrap">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-brand-cream/60">
                  <TableHead className="text-brand-navy">Name</TableHead>
                  <TableHead className="text-brand-navy">Email</TableHead>
                  <TableHead className="text-brand-navy">Role</TableHead>
                  <TableHead className="text-brand-navy">Status</TableHead>
                  <TableHead className="text-brand-navy hidden md:table-cell">Joined</TableHead>
                  <TableHead className="text-brand-navy text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-brand-slate/60">Loading…</TableCell></TableRow>
                )}
                {!loading && members.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-brand-slate/60" data-testid="admin-team-empty">No team members yet.</TableCell></TableRow>
                )}
                {!loading && members.map((m) => {
                  const isMe = m.id === me?.id;
                  return (
                    <TableRow key={m.id} data-testid={`admin-team-row-${m.id}`}>
                      <TableCell className="font-medium text-brand-navy">
                        <div className="flex items-center gap-2">
                          {m.role === "admin" ? <ShieldCheck size={14} className="text-brand-orange" /> : <UserIcon size={14} className="text-brand-slate/50" />}
                          {m.name}
                          {isMe && <span className="text-[10px] uppercase tracking-[0.2em] text-brand-orange ml-2">You</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-brand-slate">{m.email}</TableCell>
                      <TableCell>
                        <select
                          value={m.role}
                          disabled={isMe}
                          onChange={async (e) => {
                            try {
                              const { data } = await api.patch(`/admin/team/${m.id}`, { role: e.target.value });
                              setMembers((arr) => arr.map((x) => (x.id === m.id ? { ...x, ...data } : x)));
                              toast.success(`Role set to ${data.role}`);
                            } catch (err) {
                              toast.error(formatApiError(err.response?.data?.detail) || "Failed to update role");
                            }
                          }}
                          className="rounded-full text-xs font-medium border border-brand-navy/15 bg-white text-brand-navy px-3 py-1.5 focus:border-brand-orange focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid={`admin-team-role-${m.id}`}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        {m.active ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 rounded-full">Active</Badge>
                        ) : (
                          <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 rounded-full">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-brand-slate/70 text-sm">
                        {formatDate(m.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setEditing(m)}
                            className="text-brand-navy/70 hover:text-brand-orange p-1"
                            aria-label="Edit"
                            title="Edit"
                            data-testid={`admin-team-edit-${m.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => toggleActive(m)}
                            disabled={isMe}
                            className="text-xs font-semibold text-brand-navy/80 hover:text-brand-orange disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`admin-team-toggle-${m.id}`}
                          >
                            {m.active ? "Deactivate" : "Activate"}
                          </button>
                          <button
                            onClick={() => removeMember(m)}
                            disabled={isMe}
                            className="text-brand-slate/60 hover:text-red-600 p-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Delete"
                            data-testid={`admin-team-delete-${m.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      <AddMemberDialog open={openForm} onOpenChange={setOpenForm} onCreated={onMemberCreated} />
      <EditMemberDialog
        member={editing}
        isMe={editing?.id === me?.id}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          setMembers((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
          setEditing(null);
        }}
      />
      <ConfirmDialog
        open={!!memberToDelete}
        onOpenChange={(o) => !o && setMemberToDelete(null)}
        title="Delete this team member?"
        description={memberToDelete ? `${memberToDelete.name} (${memberToDelete.email}) will lose access. This cannot be undone.` : ""}
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDeleteMember}
      />
    </div>
  );
}

function EditMemberDialog({ member, isMe, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (member) {
      setForm({ name: member.name || "", email: member.email || "", password: "" });
    }
  }, [member]);

  if (!member) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim().toLowerCase();
    if (trimmedName && trimmedName !== member.name) payload.name = trimmedName;
    if (trimmedEmail && trimmedEmail !== member.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        toast.error("Enter a valid email.");
        return;
      }
      payload.email = trimmedEmail;
    }
    if (form.password) {
      if (form.password.length < 6) {
        toast.error("Password must be at least 6 characters.");
        return;
      }
      payload.password = form.password;
    }
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing to update.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.patch(`/admin/team/${member.id}`, payload);
      toast.success(`${data.name} updated`);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="admin-team-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Edit {member.name}{isMe && " (you)"}</DialogTitle>
          <DialogDescription>
            Update name, email, or reset password. Leave password blank to keep it unchanged.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-2 space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-name" className="text-sm text-brand-navy">Full name</Label>
            <Input id="edit-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} required data-testid="admin-team-edit-name" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-email" className="text-sm text-brand-navy">Email (login)</Label>
            <Input id="edit-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} required data-testid="admin-team-edit-email" />
            <span className="text-[11px] text-brand-slate/60">
              {isMe ? "Changing this will change the email you use to sign in." : "This is the email this teammate uses to sign in."}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-password" className="text-sm text-brand-navy">New password (optional)</Label>
            <Input id="edit-password" type="text" value={form.password} onChange={(e) => update("password", e.target.value)} className={inputCls} placeholder="" minLength={6} data-testid="admin-team-edit-password" />
            <span className="text-[11px] text-brand-slate/60">Min 6 characters. Leave blank to keep current password.</span>
          </div>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream"
              data-testid="admin-team-edit-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-brand-orange text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover disabled:opacity-60"
              data-testid="admin-team-edit-submit"
            >
              {submitting ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AddMemberDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const reset = () => setForm({ name: "", email: "", password: "", role: "member" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/team", form);
      onCreated(data);
      reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add member");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="admin-team-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Add team member</DialogTitle>
          <DialogDescription>
            They'll be able to sign in at <code>/admin/login</code> and work on leads.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-2 space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="t-name" className="text-sm text-brand-navy">Full name</Label>
            <Input id="t-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} required data-testid="admin-team-input-name" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="t-email" className="text-sm text-brand-navy">Email</Label>
            <Input id="t-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} required data-testid="admin-team-input-email" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="t-password" className="text-sm text-brand-navy">Temporary password</Label>
            <Input id="t-password" type="text" value={form.password} onChange={(e) => update("password", e.target.value)} className={inputCls} required minLength={6} data-testid="admin-team-input-password" />
            <span className="text-[11px] text-brand-slate/60">Share this with the new member. They should change it after first login (coming soon).</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="t-role" className="text-sm text-brand-navy">Role</Label>
            <select
              id="t-role"
              value={form.role}
              onChange={(e) => update("role", e.target.value)}
              className="rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none"
              data-testid="admin-team-input-role"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream"
              data-testid="admin-team-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-brand-orange text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover disabled:opacity-60"
              data-testid="admin-team-submit-button"
            >
              {submitting ? "Adding…" : "Add member"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}
